<template>
  <div class="fixed inset-0 overflow-hidden">
    <!-- Toasts: bottom-right with the same 16px gap to the right and bottom
         edges (explicit here so the utilities are always generated). -->
    <UApp :locale="uiLocale" :toaster="{ position: 'bottom-right', ui: { viewport: 'right-4 bottom-4' } }">
      <!-- Account lock: before the sync blocker (same layer), so a running drain covers its
           Retry and Log out until the drain ends. -->
      <account-inactive-lock />

      <!-- Sync blocker: swallows input while a data sync runs. -->
      <div
        v-if="store.syncStatus === 'inProgress'"
        class="fixed inset-0 z-50 flex items-center justify-center bg-(--app-scrim)"
      >
        <div class="flex items-center gap-3 rounded-lg border border-default bg-default px-6 py-5">
          <UIcon name="i-lucide-loader-circle" class="size-6 shrink-0 animate-spin" />
          <span>{{ t('general.sync_message') }}</span>
        </div>
      </div>

      <!-- Offline-duration lock: non-dismissable, with the 20-tap hidden unlock (support
           escape hatch). An inactive account shows its own lock instead. -->
      <offline-duration-lock />

      <notification />
      <connection-management />
      <contract v-if="requiresContract" />
      <router-view />
    </UApp>
  </div>
</template>

<script setup lang="ts">
import { provide, watch, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { en as uiEn } from '@nuxt/ui/locale';
import { uiSr } from 'app-components';
import { I18n, Shift, connectionService, AppEvent, localNetworkService, websocketsService, ICardReadResult, IWorkingDayCloseResult, CardReadService, Device, IAppEventType, Settings, ILogEntry, OrderRequest, OrdersHubService, Store, TenantService } from 'app-core';
import { emitter } from '@/utilities/emitter'
import { markOrderRequestAlerted, clearOrderRequestAlerted } from '@/utilities/orderRequestAlerts'
import { shiftBroadcastToast } from '@/utilities/shiftToast'
import { useToast } from '@/composables/useToast';
import { useRouter } from 'vue-router';
import { handleSheetBack, registerSheet, unregisterSheet } from '@/utilities/sheetRegistry';
import { scanBarcode } from '@/utilities/barcodeScanner';
import { useDisplay } from '@/composables/useDisplay';
import { useConnectionMonitor } from '@/composables/useConnectionMonitor';
import { useSystemBars } from '@/composables/useSystemBars';
import { useAppStore } from '@/stores/useAppStore';
import { useLog } from '@/composables/useLog';
import { CapacitorUtils } from 'capacitor-utils';
import { App } from '@capacitor/app';
import Notification from '@/views/Notification.vue';
import Contract from '@/views/Contract.vue';
import AccountInactiveLock from '@/views/AccountInactiveLock.vue';
import OfflineDurationLock from '@/views/OfflineDurationLock.vue';
import ConnectionManagement from '@/views/ConnectionManagement.vue'

provide('emitter', emitter)

// Kit seams (AdminFeature.md redesign AD-3): the shared CodSheet consumes the
// app's breakpoint term and hardware-back registry through these injections.
provide('kontyIsMobile', useDisplay().mobile)
provide('kontySheetRegistry', { register: registerSheet, unregister: unregisterSheet })

useConnectionMonitor()
useSystemBars()

const { toast } = useToast()

// Kit seam (P4.2): shared forms toast through the app's own policy
// (single-toast replacement, POS palette) instead of the kit fallback.
provide('kontyToast', (message: string, status: 'success' | 'error') => toast(message, status))

// Kit seam (P4.5): the shared ProductSearch scans through the mlkit flow.
provide('kontyBarcodeScanner', { scan: scanBarcode })
const { t, locale: i18nLocale } = useI18n()
const router = useRouter()

// Nuxt UI internal strings (select/calendar/pagination empty states). No sr/ba/me
// pack exists upstream — the kit's `uiSr` is Serbian wording over the bundled `hr`
// pack (its `hr` code, and so reka's number/date behaviour, unchanged).
const uiLocale = computed(() => (i18nLocale.value === 'en' ? uiEn : uiSr))
const { initializeLogging, loggingCleanup } = useLog()
const store = useAppStore()

const requiresContract = I18n.c('general.requiresContract')
const accountInactive = computed(() => TenantService.isInactive(store.tenant))

// Audio for orders hub alerts (delivery + QR). Always register the listener —
// the 'ordersHubAlert' event is only emitted when a delivery/QR event arrives
// from the backend, which only happens when the module is enabled server-side.
const ordersHubAudio = new Audio('/audio/alert1.mp3')

// Request persistent storage for site
if (navigator.storage && navigator.storage.persist) {
  navigator.storage.persist();
}

AppEvent.registerDefaultCallback(event => {
  // Gate only the backend-origin delivery events on connectivity. `isOnline` is
  // false whenever sync is failing — even with the internet up — and Pusher stays
  // live regardless, so without this gate orderTicket* could drive fireToKitchen /
  // fiscal storno against an un-reconciled store. The syncTerminals UI-refresh emits
  // below must run regardless, so a LAN peer's changes refresh the screen even while
  // this terminal is offline or mid-sync (H9). Dropped orderTicket* self-heal via the
  // 60s Orders Hub poll.
  const backendOnly = event.type === 'orderTicket'
    || event.type === 'orderTicketCancel'
    || event.type === 'orderTicketStatus'
  if (backendOnly && !connectionService.isOnline) return

  if (event.type === 'orderTicket') {
    // First ticket for a previously-inactive tenant (e.g. a configured
    // delivery source without the module flag): light up the hub + poller now.
    Store.set('ordersHubActive', true)
    emitter.emit('orderTicketReceived', event.data)
    emitter.emit('ordersHubAlert')
    return
  }

  if (event.type === 'orderTicketCancel') {
    emitter.emit('orderTicketCancelled', event.data)
    return
  }

  if (event.type === 'orderTicketStatus') {
    emitter.emit('orderTicketStatusUpdate', event.data)
    return
  }

  if (event.type === 'syncTerminals') {
    const ev = event as AppEvent<IAppEventType['syncTerminals']['request']>

    if (ev.data.model === 'KitchenTicket') emitter.emit('kitchenTicketUpdated')

    if (ev.data.model === 'OrderRequest') {
      // Incoming QR order request — store as OrderRequest, not Order
      for (const item of ev.data.items) {
        if (item.action !== 'delete' && item.record) {
          // A real incoming/updated request — assert ordersHubActive so the
          // hub surfaces without a reload (QR-enabled tenants are already
          // active; this also covers a late-enabled venue). Never on 'delete'.
          Store.set('ordersHubActive', true)
          const request = new OrderRequest(item.record)
          request.$save()
          // Route the alert through the shared ledger so the sound/toast fires
          // exactly once per request even if the 60s poll discovered it first
          // (the push can arrive after a poll tick). Only a pending request
          // alerts: approved/rejected restates (a peer handled it) stay silent.
          if (request.isPending() && markOrderRequestAlerted(item.record.id)) {
            emitter.emit('orderRequestPending', request)
            emitter.emit('ordersHubAlert')
          }
        }
        if (item.action === 'delete' && item.record?.id) {
          clearOrderRequestAlerted(item.record.id)
          const existing = OrderRequest.find(item.record.id) as OrderRequest | undefined
          existing?.$delete()
          emitter.emit('orderRequestRemoved')
        }
      }
    }

    if (ev.data.model === 'Order') {
      emitter.emit('ordersUpdated')
    }

    if (ev.data.model === 'TableSession') {
      emitter.emit('tableSessionsUpdated')
    }

    if (ev.data.model === 'Shift') {
      const toastKey = shiftBroadcastToast(ev.data.items.map(i => i.record), store.cashRegister?.id, !!Shift.current())
      if (toastKey) toast(t(toastKey))
    }
  }
})

// Hydrate the Orders Hub on app start (catch-up for missed WebSocket pushes).
// Runs at tenant level — no user login needed. Skipped for tenants the hub
// isn't relevant to (no delivery / QR / tickets) so they never hit the endpoint;
// /initialize will have set ordersHubActive before this runs.
if (connectionService.isOnline && store.ordersHubActive) {
  OrdersHubService.fetchSnapshot()
}

emitter.on('ordersHubAlert', () => {
  ordersHubAudio.play().catch(() => {})
})

// The auth screens are always-dark by design (P01/P02). Scoping `dark` at the
// document root (not just a local wrapper class) keeps every portal — toasts,
// sheets, context menus — on the dark palette while those screens are up.
const ALWAYS_DARK_ROUTES = ['tenantLogin', 'tenantRegister', 'userLogin']
const forceDark = computed(() => ALWAYS_DARK_ROUTES.includes(String(router.currentRoute.value.name)))

watch([() => store.isDark, forceDark], ([dark, forced]) => {
  document.documentElement.classList.toggle('dark', dark || forced)
}, { immediate: true })

// Through the card-terminal seam, not the plugin directly: the same object
// CardReadService sends requests to delivers the answers (the native plugin on
// Android, the E2E suite's double on a test shell).
CardReadService.terminal().addListener('cardReadCompleted', (data: ICardReadResult) => {
  emitter.emit('cardReadCompleted', data)
})
CardReadService.terminal().addWorkingDayCloseListener((data: IWorkingDayCloseResult) => {
  emitter.emit('workingDayClosed', data)
})

App.addListener('resume', () => {
  if(!Device.isMobile()) return

  if(websocketsService.connectionStatus !== 'connected') websocketsService.reload()
  if(!localNetworkService.connectionStatus?.connected) localNetworkService.reload()
})

// Android hardware back (policy: docs/NavigationGuards.md): the topmost open
// sheet pops a step or closes (guard-respecting); any other open dialog
// swallows the press (it owns its dismissal via its buttons/backdrop);
// otherwise navigate back, exiting the app at the history root.
App.addListener('backButton', ({ canGoBack }) => {
  // The sync blocker and the offline and account locks are plain overlays (no
  // dialog role) — back must not navigate beneath them or exit the app mid-sync.
  if (store.syncStatus === 'inProgress' || store.lockedReason || accountInactive.value) return
  if (handleSheetBack()) return
  if (document.querySelector('[role="dialog"]')) return
  if (canGoBack) {
    router.back()
  } else {
    App.exitApp()
  }
})

initializeLogging()

onUnmounted(() => {
  loggingCleanup();
})
</script>
